import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';

/**
 * Searchable IANA time-zone picker. Stores the IANA id (e.g. "America/New_York")
 * — exactly what the API's TimeZone column expects — and labels each option with
 * its current UTC offset as a hint. Reusable across warehouse / tenant / user
 * forms.
 *
 * The zone list comes from Intl.supportedValuesOf('timeZone') on modern engines
 * (Chromium, which is what the PWA targets); older engines fall back to a small
 * curated list.
 */

const FALLBACK_ZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Mexico_City',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Australia/Sydney',
];

function supportedTimeZones(): string[] {
  try {
    const withValues = Intl as typeof Intl & {
      supportedValuesOf?: (key: 'timeZone') => string[];
    };
    const zones = withValues.supportedValuesOf?.('timeZone');
    if (zones && zones.length > 0) return zones;
  } catch {
    // fall through to the curated list
  }
  return FALLBACK_ZONES;
}

function offsetLabel(timeZone: string, at: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

// Built once at module load — the zone list and current offsets don't change
// meaningfully within a session.
const BASE_OPTIONS: { value: string; label: string }[] = (() => {
  const now = new Date();
  return supportedTimeZones().map((tz) => {
    const offset = offsetLabel(tz, now);
    return { value: tz, label: offset ? `${tz} (${offset})` : tz };
  });
})();

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

export function TimeZoneSelect({ value, onChange, ...rest }: Props) {
  const { t } = useTranslation();

  // Keep the current value selectable even if the runtime didn't list it
  // (e.g. a legacy/imported zone id).
  const data = useMemo(() => {
    if (value && !BASE_OPTIONS.some((o) => o.value === value)) {
      return [{ value, label: value }, ...BASE_OPTIONS];
    }
    return BASE_OPTIONS;
  }, [value]);

  return (
    <Select
      {...rest}
      data={data}
      value={value || null}
      onChange={(v) => onChange(v ?? '')}
      searchable
      allowDeselect={false}
      nothingFoundMessage={t('shell.noMatch')}
      maxDropdownHeight={280}
      comboboxProps={{ shadow: 'md' }}
    />
  );
}
